import { cn, focusRing } from "@simpro/ui";
import { useQuery } from "@tanstack/react-query";
import { ArrowUpRight } from "lucide-react";
import { Link } from "react-router";
import { salonNow, salonProfileQuery, useSalonClock, weekRows } from "@/features/salon";
import { chooseBranch } from "@/lib/branch";
import { formatPhone } from "@/lib/formatPhone";
import { AppearanceSwitch } from "@/shared/components/AppearanceSwitch";
import { useMediaQuery } from "@/shared/hooks/useMediaQuery";
import { page } from "@/shared/styles/layout";

const EXPLORE = [
  { to: "/", label: "Explore" },
  { to: "/services", label: "Services" },
  { to: "/gallery", label: "Gallery" },
  { to: "/appointments", label: "My bookings" },
  { to: "/book", label: "Book a visit" },
] as const;

/**
 * The foot of every page outside the booking flow (design note salon-booking-organic-wellness.md
 * section 6 item 11). It is the board's green band rising out of the cream on rounded shoulders, with
 * its line set large in the display face, the second half in italic in the accent's soft step, then
 * three columns: where to find the salon, its week, and the way around the site. The line is the same
 * for every salon (open question 3); the salon's name is in the copyright row. On a desktop it also holds
 * Auto / Light / Dark, so a visitor who is not signed in can reach it (follow-up F18); on a phone that
 * lives in Account → Preferences. The bottom margin on phones keeps it clear of the floating Book
 * button.
 */
export function SiteFooter() {
  const { data: salon } = useQuery(salonProfileQuery());
  const { clock, timeZone } = useSalonClock();
  const desktop = useMediaQuery("(min-width: 1024px)");
  // A salon with several branches and none chosen shows its picker instead (FR-SA-245).
  if (!salon?.branch) return null;
  const { branch } = salon;
  const rows = weekRows(salon.hours, salonNow(timeZone).day, clock);
  const link = cn("link-draw rounded-sm text-on-bar/80 hover:text-on-bar", focusRing);

  return (
    <footer className="on-ink band-top bg-bar text-on-bar mt-24 md:mt-32">
      <div className={cn(page, "flex flex-col gap-12 pt-16 pb-32 lg:gap-16 lg:pt-20 lg:pb-12")}>
        <p className="display-section text-on-bar text-[clamp(2.5rem,6vw,5.5rem)]">
          Breathe in. <em className="text-ink-link">Book well.</em>
        </p>

        <div className="grid gap-10 text-sm md:grid-cols-3 lg:text-[15px]">
          <section aria-labelledby="footer-visit" className="flex flex-col gap-3">
            <h2 id="footer-visit" className="eyebrow text-on-bar/60">
              Visit
            </h2>
            <address className="text-on-bar/80 leading-relaxed not-italic">
              {[branch.addressLine1, branch.addressLine2, branch.city, branch.postalCode]
                .filter(Boolean)
                .join(", ")}
            </address>
            <a className={cn(link, "self-start")} href={`tel:${branch.phone}`}>
              {formatPhone(branch.phone)}
            </a>
            {branch.mapUrl ? (
              <a
                className={cn(link, "inline-flex items-center gap-1 self-start")}
                href={branch.mapUrl}
                target="_blank"
                rel="noreferrer"
              >
                Get directions
                <ArrowUpRight className="size-4" aria-hidden="true" />
              </a>
            ) : null}
            {/* The way to another branch for everyone, including a customer the counter code brought here (FR-SA-245). */}
            {salon.branchCount > 1 ? (
              <button type="button" onClick={chooseBranch} className={cn(link, "self-start text-left")}>
                Our other branches
              </button>
            ) : null}
          </section>

          {rows.length > 0 ? (
            <section aria-labelledby="footer-hours" className="flex flex-col gap-3">
              <h2 id="footer-hours" className="eyebrow text-on-bar/60">
                Hours
              </h2>
              <dl className="flex flex-col gap-1.5">
                {rows.map((row) => (
                  <div key={row.label} className="flex justify-between gap-4">
                    <dt className={row.today ? "text-on-bar font-semibold" : "text-on-bar/70"}>{row.label}</dt>
                    <dd className={cn("tabular-nums", row.today ? "text-on-bar font-semibold" : "text-on-bar/70")}>
                      {row.value}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          ) : null}

          <nav aria-labelledby="footer-explore" className="flex flex-col gap-3">
            <h2 id="footer-explore" className="eyebrow text-on-bar/60">
              Explore
            </h2>
            <ul className="flex flex-col gap-2">
              {EXPLORE.map((item) => (
                <li key={item.to}>
                  <Link to={item.to} viewTransition className={link}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="border-on-bar/15 flex flex-col gap-4 border-t pt-6 lg:flex-row lg:items-center lg:justify-between">
          <span className="text-on-bar/60 text-xs">
            © {new Date().getFullYear()} {salon.displayName}
          </span>
          {desktop ? (
            <div className="flex items-center gap-3">
              <span id="footer-appearance" className="text-on-bar/70 text-sm">
                Appearance
              </span>
              <AppearanceSwitch labelledBy="footer-appearance" onBar />
            </div>
          ) : null}
        </div>
      </div>
    </footer>
  );
}
